import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { TENANTS, getAll, getSelectedId, isAppReady } from '../../store/reducers';
import { auth } from '../../store/auth/auth.selectors';
import { filter, withLatestFrom, map } from 'rxjs/operators';
import { CAREAPP_MANAGER, GLOBAL_ROLES, TENANT_ADMINISTRATOR } from '@careapp-group/core';
import { TenantDto } from '@careapp-group/ng-sdk';
import * as actions from '../../store/actions';

/**
 * User must have a global role or be a CareApp Manager
 */
@Injectable()
export class TenantManagerGuard  {
  private appReady: Observable<boolean>;
  private authenticated: Observable<boolean>;
  private globalRoles: Observable<string[]>;
  private tenants: Observable<TenantDto[]>;
  private selectedTenantId: Observable<String>;
  constructor(private store: Store<any>) {
    this.appReady = this.store.select(isAppReady);
    this.globalRoles = this.store.select(auth.getRoles);
    this.authenticated = this.store.select(auth.getLoggedIn);
    this.tenants = this.store.select(getAll(TENANTS));
    this.selectedTenantId = this.store.select(getSelectedId(TENANTS));
  }

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.appReady.pipe(
      filter((ready) => ready),
      withLatestFrom(this.authenticated, this.globalRoles, this.tenants, this.selectedTenantId),
      map(([ready, loggedIn, globalRoles, tenants, requestedTenantId]) => {
        if (loggedIn) {
          // Global role
          if (globalRoles && globalRoles.some((r) => GLOBAL_ROLES.includes(r))) {
            return true;
          }
          // Tenant Administrator
          const isAdmin = tenants
            .filter((tenant) => tenant.id === requestedTenantId)
            .some(
              (tenant) =>
                tenant?.permission?.role === TENANT_ADMINISTRATOR &&
                tenant?.permission?.enabled === true
            );
          if (isAdmin) {
            return true;
          }
        }
        this.store.dispatch(
          actions.NavigateAction({
            path: ['/'],
            reason: `User must manage a tenant to view this page`,
          })
        );
        return false;
      })
    );
  }
}
